import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCircle2, Loader2, ScanFace, ShieldCheck } from "lucide-react";

const FACEAPI_CDN = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
// Models hosted on jsdelivr (mirror of justadudewhohacks/face-api.js weights).
// raw.githubusercontent.com blocks large-file cross-origin loads on many mobile
// browsers — jsdelivr is CDN-cached and always CORS-friendly.
const MODELS_URL = "https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights";

// Steps (in order): kept the same 6-step flow but detection thresholds are loosened
// below so blinks, head-turns, and smiles register on the FIRST natural attempt.
const STEPS = [
  { id: "look", label: "Look straight at the camera", target: "detect_face", hint: "Center your face in the oval" },
  { id: "blink1", label: "Blink once", target: "blink", hint: "Just close and open your eyes normally" },
  { id: "blink2", label: "Blink again", target: "blink", hint: "One more quick blink" },
  { id: "left", label: "Turn your head LEFT", target: "turn_left", hint: "A small turn to the left is enough" },
  { id: "right", label: "Turn your head RIGHT", target: "turn_right", hint: "Now a small turn to the right" },
  { id: "smile", label: "Smile for the camera!", target: "smile", hint: "A light smile is enough" },
];

// Eye Aspect Ratio (EAR) formula
function ear(eye) {
  const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  return (d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2.0 * d(eye[0], eye[3]));
}

export function LiveFaceKYC({ onComplete, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    blinks: 0,
    headTurns: 0,
    smileDetected: false,
    frames: 0,
    startedAt: null,
    earHistory: [],
    lastEye: "open",
    lastYawSign: 0,
    maxYawAbs: 0,
  });

  const [loading, setLoading] = useState(true);
  const [modelReady, setModelReady] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState("Loading face detection engine…");
  const [snapshot, setSnapshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Load face-api.js CDN once
  useEffect(() => {
    if (window.faceapi) { loadModels(); return; }
    const s = document.createElement("script");
    s.src = FACEAPI_CDN;
    s.async = true;
    s.onload = () => loadModels();
    s.onerror = () => { setLoading(false); toast.error("Failed to load face engine"); };
    document.body.appendChild(s);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadModels = async () => {
    try {
      setStatus("Loading face models…");
      const faceapi = window.faceapi;
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL),
        faceapi.nets.faceExpressionNet.loadFromUri(MODELS_URL),
      ]);
      setModelReady(true);
      setStatus("Starting your camera…");
      await startCamera();
    } catch (e) {
      console.error("[LiveKYC] model load failed", e);
      toast.error("Could not load face models — check your internet and reload the page.");
      setStatus("Could not load face models — check your internet and reload.");
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 480, height: 480, facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      stateRef.current.startedAt = Date.now();
      setLoading(false);
      setStatus("Follow the on-screen instructions");
      detectLoop();
    } catch (e) {
      console.error(e);
      toast.error("Camera access denied. Please allow camera and retry.");
      setLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const detectLoop = async () => {
    const faceapi = window.faceapi;
    if (!faceapi || !videoRef.current) return;

    const runDetection = async () => {
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) {
        rafRef.current = requestAnimationFrame(runDetection);
        return;
      }
      try {
        // Larger inputSize + lower scoreThreshold = detects faces more reliably
        // in low light / off-angle poses, which is where most users get stuck.
        const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.35 });
        const det = await faceapi
          .detectSingleFace(videoRef.current, options)
          .withFaceLandmarks()
          .withFaceExpressions();

        if (det) {
          stateRef.current.frames += 1;
          const landmarks = det.landmarks;
          const leftEye = landmarks.getLeftEye();
          const rightEye = landmarks.getRightEye();
          const avgEAR = (ear(leftEye) + ear(rightEye)) / 2;
          stateRef.current.earHistory.push(avgEAR);
          if (stateRef.current.earHistory.length > 12) stateRef.current.earHistory.shift();

          // Blink detection — MUCH more forgiving thresholds so a natural quick
          // blink registers on the first try. Old: 0.20 / 0.28. New: 0.24 / 0.26.
          if (avgEAR < 0.24 && stateRef.current.lastEye === "open") {
            stateRef.current.lastEye = "closed";
          } else if (avgEAR > 0.26 && stateRef.current.lastEye === "closed") {
            stateRef.current.lastEye = "open";
            stateRef.current.blinks += 1;
          }

          // Head yaw estimation: relative x-offset of nose tip vs face center.
          // Loosened threshold (0.18 vs 0.28) so a small natural head turn counts.
          const box = det.detection.box;
          const nose = landmarks.getNose()[3]; // tip
          const cx = box.x + box.width / 2;
          const yawSign = Math.sign(nose.x - cx);
          const yawAbs = Math.abs(nose.x - cx) / (box.width / 2); // 0..1
          if (yawAbs > 0.18) {
            if (yawSign !== 0 && yawSign !== stateRef.current.lastYawSign) {
              stateRef.current.headTurns += 1;
              stateRef.current.lastYawSign = yawSign;
            }
          } else if (yawAbs < 0.08) {
            stateRef.current.lastYawSign = 0; // recentre
          }
          stateRef.current.maxYawAbs = Math.max(stateRef.current.maxYawAbs, yawAbs);

          // Smile detection — accept a light smile (0.5 vs 0.75)
          const smile = det.expressions?.happy || 0;
          if (smile > 0.5) stateRef.current.smileDetected = true;

          advanceStep();
        }
      } catch (err) {
        // Silent — continue loop
      }
      rafRef.current = requestAnimationFrame(runDetection);
    };
    runDetection();
  };

  const advanceStep = () => {
    setStepIdx((idx) => {
      if (idx >= STEPS.length) return idx;
      const s = STEPS[idx];
      const st = stateRef.current;
      let done = false;
      if (s.target === "detect_face") done = st.frames >= 3;
      else if (s.target === "blink" && s.id === "blink1") done = st.blinks >= 1;
      else if (s.target === "blink" && s.id === "blink2") done = st.blinks >= 2;
      else if (s.target === "turn_left") done = st.headTurns >= 1;
      else if (s.target === "turn_right") done = st.headTurns >= 2;
      else if (s.target === "smile") done = st.smileDetected;
      if (done) {
        const next = idx + 1;
        if (next === STEPS.length) {
          // Capture snapshot and stop
          captureSnapshot();
        }
        return next;
      }
      return idx;
    });
  };

  const captureSnapshot = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setSnapshot(dataUrl);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    stopCamera();
    setStatus("Liveness verified! Review your snapshot and continue.");
  };

  const handleSubmit = async ({ id_document_b64, full_name, document_type }) => {
    if (!snapshot) return;
    const st = stateRef.current;
    const durationSec = (Date.now() - (st.startedAt || Date.now())) / 1000;
    setSubmitting(true);
    try {
      await onComplete({
        selfie_b64: snapshot,
        id_document_b64,
        full_name,
        document_type,
        liveness: {
          blinks_detected: st.blinks,
          head_turns_detected: st.headTurns,
          smile_detected: st.smileDetected,
          frames_captured: st.frames,
          duration_seconds: Number(durationSec.toFixed(2)),
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const st = stateRef.current;
  const currentStep = STEPS[Math.min(stepIdx, STEPS.length - 1)];
  const allDone = stepIdx >= STEPS.length;

  return (
    <div data-testid="live-kyc-panel" className="nb-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <ScanFace className="text-nb-cyan" size={20}/>
        <h2 className="font-heading text-white text-xl">Live Face Verification</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Camera */}
        <div className="relative rounded-2xl overflow-hidden bg-black/60 border border-white/10 aspect-square">
          {snapshot ? (
            <img data-testid="live-kyc-snapshot" src={snapshot} alt="Snapshot" className="w-full h-full object-cover"/>
          ) : (
            <video data-testid="live-kyc-video" ref={videoRef} playsInline muted className="w-full h-full object-cover"/>
          )}
          {!snapshot && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-2/3 h-4/5 border-2 border-[#8AE234]/40 rounded-[50%] shadow-[0_0_60px_rgba(138,226,52,0.15)]"/>
            </div>
          )}
          {loading && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center gap-2 text-white text-sm">
              <Loader2 className="animate-spin" size={16}/> {status}
            </div>
          )}
        </div>

        {/* Instructions & progress */}
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Step {Math.min(stepIdx + 1, STEPS.length)} of {STEPS.length}</div>
          <div data-testid="live-kyc-step-label" className="font-heading text-white text-2xl mt-1">{allDone ? "Verification complete" : currentStep.label}</div>
          <div className="text-sm text-slate-400 mt-1">{allDone ? "Please upload your ID document below to finish." : currentStep.hint}</div>

          <div className="mt-4 space-y-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-2 text-sm ${i < stepIdx ? "text-[#8AE234]" : i === stepIdx ? "text-white" : "text-slate-500"}`}>
                {i < stepIdx ? <CheckCircle2 size={14}/> : <Camera size={14}/>} {s.label}
              </div>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-center">
            <Stat label="Blinks" value={st.blinks}/>
            <Stat label="Head turns" value={st.headTurns}/>
            <Stat label="Smile" value={st.smileDetected ? "✓" : "—"}/>
          </div>

          {allDone && (
            <IdUploadForm submitting={submitting} onSubmit={handleSubmit} onCancel={onCancel}/>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-black/40 border border-white/5 p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-lg font-heading text-white mt-1">{value}</div>
    </div>
  );
}

function IdUploadForm({ submitting, onSubmit, onCancel }) {
  const [fullName, setFullName] = useState("");
  const [docType, setDocType] = useState("passport");
  const [idFile, setIdFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!idFile) { toast.error("Please upload your ID document"); return; }
    const id_document_b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result); r.onerror = rej;
      r.readAsDataURL(idFile);
    });
    await onSubmit({ id_document_b64, full_name: fullName.trim(), document_type: docType });
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-3 border-t border-white/10 pt-4">
      <div>
        <label className="text-xs text-slate-400 uppercase tracking-widest">Full legal name</label>
        <input data-testid="live-kyc-name" required value={fullName} onChange={(e)=>setFullName(e.target.value)} className="nb-input mt-1"/>
      </div>
      <div>
        <label className="text-xs text-slate-400 uppercase tracking-widest">Document type</label>
        <select data-testid="live-kyc-doctype" value={docType} onChange={(e)=>setDocType(e.target.value)} className="nb-input mt-1">
          <option value="passport">Passport</option>
          <option value="driver_license">Driver's License</option>
          <option value="national_id">National ID</option>
        </select>
      </div>
      <div>
        <label className="text-xs text-slate-400 uppercase tracking-widest">ID document photo</label>
        <input data-testid="live-kyc-id-file" type="file" accept="image/*" required onChange={(e)=>setIdFile(e.target.files?.[0])} className="nb-input mt-1"/>
      </div>
      <div className="flex gap-2">
        <button data-testid="live-kyc-submit" disabled={submitting} className="nb-btn-primary flex-1">
          {submitting ? <><Loader2 className="animate-spin inline mr-2" size={14}/>Verifying…</> : <><ShieldCheck size={14} className="inline mr-2"/>Complete verification</>}
        </button>
        <button type="button" onClick={onCancel} className="nb-btn-secondary">Cancel</button>
      </div>
    </form>
  );
}
